/**
 * オファー（入会と同時に使われたクーポンの割引額）の内訳タブの共有定義。
 *
 * 日次バッチ（apps/generator/ac-mens-hub-daily）が獲得実績スプシの内訳タブへ書き、
 * 予実の取り込み（apps/api/ac-mens-hub）が同じタブを読み、合計CPAに足す。
 * タブは直近の日（`OFFER_REFRESH_DAYS`）の書き込み口で、履歴の正本は BigQuery の `plan__offer_daily`。
 * 読み手は直近の日（前回の取り込みで `plan__offer_coverage` に記録した計算済みの最終日から抜けた日があれば、
 * その最終日の13日前まで広げる）だけをタブの内容で置き換え、それより前は BQ にその日の行が無い日だけ足す
 * （人がタブの古い行を消したり書き換えたりしても、Hub の履歴は変わらない）。計算済みの最終日は取り込みのたびに
 * `plan__offer_coverage` に記録し、読み出しはその日までを「内訳の行が無ければ 0」とする。
 * スプシ ID・タブ名・列の並びをこのタブを扱うコードごとに別々に持つと、片方だけ変えたときに
 * 「書いたのに読めない」「別の列を金額として読む」壊れ方をするため、ここを唯一の正本にする。
 * 見出しの一致・データ行の解析・行のキー・最終計算日時の解釈は `offer-sheet-rows.ts`、暦の判定・日数の加減・
 * 日本時間の日時の読み書きは `calendar-date.ts` に置き、書き手と読み手が同じ関数を使う。日付とシリアル値の変換は
 * 読む向き（`sheetsSerialToDateString`）と書く向き（`dateKeyToSheetsSerial`）をこのファイルに並べる。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー（入会時クーポンの割引額）の内訳タブの所在と列の並びを、書き込む日次バッチと、読み取って合計CPAに足す予実の取り込み（Hub API）の間で共有する
 * @graph-connects none
 */
import { isCalendarDate } from './calendar-date.js';
/**
 * 獲得実績スプレッドシートの ID。広告費・獲得数の実績（シート1）と、
 * 日次バッチが書くオファーの内訳タブが同居する。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 獲得実績（広告費・獲得数）とオファー内訳を置くスプレッドシートを特定する
 * @graph-connects none
 */
export const ACQUISITION_SPREADSHEET_ID = '1rzPzMTQku5RpSvB4pw_f8Y6cb4RFbCr3DO012-s_wnQ';
/**
 * 日次バッチが書き込むオファー内訳のタブ名。末尾の「_自動」は、自動書き込み専用のタブで
 * 人が手で編集するタブではない（毎朝書き直される）ことを示す。タブが無ければ日次バッチが作る。
 * 計算期間外の行も値として書き戻すため、人が入れた数式は値に固定される。手で足したい費用は
 * シート1 の「その他広告費」へ入れる。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 日次バッチが毎朝書き直すオファー内訳のタブを特定する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_SHEET_NAME = 'オファー内訳_自動';
/**
 * オファー内訳タブの 1 行目（見出し）。A 列から順に並ぶ。
 * 書き手はこの見出しと完全一致しないタブには書かない（タブが無ければ作り、完全に空のタブにだけ、
 * この見出しを書いてから書く）。見出しの検査は A〜G の 7 列だけで、最終計算日時の列は含めない。
 * 読み手（予実の取り込み）も A〜G が完全一致しないタブは取り込まない（判定は両方とも `isOfferBreakdownHeader`）。
 * 列の位置はこの並びで決める。
 *
 * | 列 | 見出し | 中身 |
 * |---|---|---|
 * | A | 日付 | 入会日（日本時間）。日付シリアル値（数値）で、表示形式は yyyy-mm-dd |
 * | B | クーポン名 | coupon_masters.name |
 * | C | 割引内容 | 月額の割引率または割引額 |
 * | D | 数えた月数 | 1件あたり何ヶ月分の割引をオファーとして数えたか |
 * | E | 件数 | 入会と同時にそのクーポンが使われた件数 |
 * | F | 1ヶ月分の割引額 | 入会月の割引額の合計（税抜） |
 * | G | オファー額 | 1ヶ月分の割引額 × 数えた月数 の合計（税抜） |
 *
 * H 列（`OFFER_BREAKDOWN_EXCLUSION_REASON_HEADER`）は人が読むための補足で、見出しの検査にも読み手の解析にも入れない
 * （H 列が無いタブも読める。合計CPAに含めるかの判定は H 列ではなくクーポン名の共有の判定 `offerCpaExclusionReason`）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳タブの見出しと列の並びを定義する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_HEADERS = [
    '日付',
    'クーポン名',
    '割引内容',
    '数えた月数',
    '件数',
    '1ヶ月分の割引額',
    'オファー額',
];
/**
 * 内訳タブの H 列（見出し `OFFER_BREAKDOWN_HEADERS` の右隣）の見出し。合計CPA（オファーあり）に含めない割引の行に、
 * 含めない理由（`offerCpaExclusionReason`）を書く（含める行は空）。日次バッチが毎朝 H1 と各行に書き、人がタブの合計
 * （シート1 の H 列の SUMIFS など）から外すのに使う。読み手（予実の取り込み）は H 列を読まない。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳タブで合計CPAに含めない割引の理由を書く列の見出しを、書き手と人が見るシートで共有する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_EXCLUSION_REASON_HEADER = 'CPAに含めない理由';
/**
 * 日次バッチが最後に計算を終えた日時の見出し。`OFFER_BREAKDOWN_LAST_COMPUTED_HEADER_CELL` に入る。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳の最終計算日時のセルの見出しを定義する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_LAST_COMPUTED_HEADER = '最終計算日時';
/**
 * 最終計算日時の見出し（`OFFER_BREAKDOWN_LAST_COMPUTED_HEADER`）を置くセル（オファー内訳タブ内の A1 形式）。
 * 見出し 7 列（A〜G）の外に置き、見出しの検査の対象にしない。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳の最終計算日時の見出しを置くセルを、書き手と読み手で共有する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_LAST_COMPUTED_HEADER_CELL = 'I1';
/**
 * 最終計算日時を置くセル（オファー内訳タブ内の A1 形式）。中身は日本時間の `YYYY-MM-DD HH:mm:ss`（文字列）。
 * 書き手は計算と書き込みが成功した回だけ更新し、次の朝はこの日付の前日までを計算済みとして、計算し直す期間の
 * 始まりを決める。読み手はこれが古ければ毎朝の更新が止まったとみなす。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳の最終計算日時の値を置くセルを、書き手と読み手で共有する
 * @graph-connects none
 */
export const OFFER_BREAKDOWN_LAST_COMPUTED_VALUE_CELL = 'I2';
/**
 * オファーの計測を始めた日（日本時間, YYYY-MM-DD）。書き手（日次バッチ）はこの日より前を計算せず、
 * 内訳タブの既存行もこの日より前の日付は読めない行として扱う（共有の解析 `readOfferSheetRows`。書き手は捨て、
 * 読み手は取り込まない）。読み手もこの日から集計し、これより前の日の
 * オファー額と合計CPA（オファーあり）は「0」ではなく「データなし」として扱う。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー（入会時クーポンの割引額）の計測を始めた日を、書き手と読み手で共有する
 * @graph-connects none
 */
export const OFFER_TRACKING_START_DATE = '2026-07-01';
/**
 * 毎朝計算し直す直近の日数（計算済みの最終日＝最終計算日時の日付の前日を含めて数える）。
 * 書き手はこの範囲（抜けた日があればそこまで）を計算し直してタブに書き、読み手はこの範囲だけをタブの内容で
 * 置き換える（範囲の中でタブから消えた行は BQ からも消し、範囲より前は BQ に無い日だけ足す）。読み手は、
 * 前回の取り込みで記録した計算済みの最終日からこの日数で数えた範囲も含め、取り込みが抜けた日の分まで置き換える。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business オファー内訳を毎朝計算し直す直近の日数を、書き手（タブを書き直す範囲）と読み手（BQ で置き換える範囲）で共有する
 * @graph-connects none
 */
export const OFFER_REFRESH_DAYS = 14;
/**
 * A1 形式のセル（例 `I2`）の列番号と行番号（どちらも 1 始まり）。A1 形式でなければ例外にする。
 * 最終計算日時のセル（`OFFER_BREAKDOWN_LAST_COMPUTED_HEADER_CELL` / `OFFER_BREAKDOWN_LAST_COMPUTED_VALUE_CELL`）を
 * 行・列に直すのに使い、書き手（タブに要る大きさ）と読み手（読む位置）が同じ解釈をするようにする。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business A1形式のセル番地を列番号と行番号に分解し、最終計算日時のセルの位置を書き手と読み手で同じに解釈する
 * @graph-connects none
 */
export function parseA1Cell(cell) {
    const match = /^([A-Z]+)([1-9]\d*)$/.exec(cell);
    const letters = match?.[1];
    const digits = match?.[2];
    if (letters === undefined || digits === undefined) {
        throw new Error(`not an A1 cell: ${JSON.stringify(cell)}`);
    }
    let column = 0;
    for (let i = 0; i < letters.length; i += 1) {
        column = column * 26 + letters.charCodeAt(i) - 64;
    }
    return { column, row: Number(digits) };
}
/**
 * 1 始まりの列番号を A1 形式の列の文字（1 → A、9 → I、27 → AA）にする（`parseA1Cell` の列の逆）。
 * 1 以上の整数でなければ例外にする。書き手と読み手が、読む範囲・書く範囲の列を同じ規則で組み立てるために置く。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 列番号をA1形式の列の文字に変換し、オファー内訳タブの範囲を書き手と読み手で同じ規則で組み立てる
 * @graph-connects none
 */
export function toColumnLetters(column) {
    if (!Number.isInteger(column) || column < 1) {
        throw new Error(`not a column number: ${String(column)}`);
    }
    let letters = '';
    for (let n = column; n > 0; n = Math.floor((n - 1) / 26)) {
        letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
    }
    return letters;
}
/**
 * Google Sheets の日付シリアル値（`UNFORMATTED_VALUE` で読んだ日付セル）を `YYYY-MM-DD` にする。
 * 獲得実績スプレッドシートの日付（シート1 の A 列・オファー内訳タブの A 列）を、書き手と読み手が同じ規則で読むために置く。
 * 60 以上は Sheets の数え方（1899-12-30 = 0）と同じ日付になる（60 = 1900-02-28、61 = 1900-03-01）。59 以下は
 * Excel の 1900 年起点（1 = 1900-01-01）で数える（予実の取り込みが使ってきた数え方。獲得実績・オファーの日付は
 * 2026 年以降なので、この範囲は読まない）。時刻を含む小数部は切り捨てる。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business スプレッドシートの日付シリアル値を YYYY-MM-DD の日付に変換し、書き手と読み手で同じ規則で読む
 * @graph-connects none
 */
export function sheetsSerialToDateString(serial) {
    const adjusted = serial > 59 ? serial - 1 : serial;
    const d = new Date(Date.UTC(1900, 0, adjusted));
    const yyyy = String(d.getUTCFullYear());
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}
/**
 * Sheets のシリアル日付の起点（1899-12-30）の UTC ミリ秒。
 * @graph-connects none
 */
const SHEETS_EPOCH_MS = Date.UTC(1899, 11, 30);
/** @graph-connects none */
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * `YYYY-MM-DD` を Google Sheets の日付シリアル値（1899-12-30 = 0 の日数）にする。`sheetsSerialToDateString` の
 * 逆向き（1900-03-01 以降の日付は往復で同じ日付に戻る）。日次バッチが内訳タブの A 列に書く値で、シート1 の A 列と同じ
 * 数値の日付にする（シート1 H 列の SUMIFS が日付で突き合わせるため）。実在しない日付は例外にする（シートに誤った
 * 日付を書かないため）。
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 日付をスプレッドシートの日付シリアル値に変換し、読む向きの変換と同じ場所で往復をそろえる
 * @graph-connects isCalendarDate [calls] 変換する日付の検証
 */
export function dateKeyToSheetsSerial(dateKey) {
    if (!isCalendarDate(dateKey)) {
        throw new Error(`not a real YYYY-MM-DD date: ${JSON.stringify(dateKey)}`);
    }
    return (Date.parse(`${dateKey}T00:00:00Z`) - SHEETS_EPOCH_MS) / MS_PER_DAY;
}
//# sourceMappingURL=offer-sheet.js.map